export type BsciDatapointValueType = 'number' | 'currency_with_year';

export interface BsciDatapointMapping {
  readonly key: string;
  readonly auditLabel: string;
  readonly dataPointId: string;
  readonly valueType: BsciDatapointValueType;
  readonly category: string;
}

/**
 * Maps BSCI audit labels to standardized datapoint IDs from @retracedgmbh/data-points-lib.
 *
 * - `key` is the property name used in the AI prompt response schema (= dataPointId).
 * - `valueType: 'currency_with_year'` means the lib expects `{ value, unit (currency code), year }`.
 * - `valueType: 'number'` means the lib expects a plain numeric value.
 */
export const BSCI_DATAPOINT_MAP: readonly BsciDatapointMapping[] = [
  // --- Key Metrics ---
  {
    key: 'total_number_of_workers',
    auditLabel: 'Total workforce',
    dataPointId: 'total_number_of_workers',
    valueType: 'number',
    category: 'Key Metrics',
  },
  {
    key: 'mininum_wage_local_currency',
    auditLabel: 'Legal minimum wage in local currency',
    dataPointId: 'mininum_wage_local_currency',
    valueType: 'currency_with_year',
    category: 'Key Metrics',
  },
  {
    key: 'lowest_wage_paid_regular_work',
    auditLabel: 'Lowest wage paid for regular work at the site',
    dataPointId: 'lowest_wage_paid_regular_work',
    valueType: 'currency_with_year',
    category: 'Key Metrics',
  },
  {
    key: 'living_wage_local_currency',
    auditLabel: 'Calculated living wage in local currency',
    dataPointId: 'living_wage_local_currency',
    valueType: 'currency_with_year',
    category: 'Key Metrics',
  },
  {
    key: 'payroll_how_many_workers_were_sampled',
    auditLabel: 'Total sample',
    dataPointId: 'payroll_how_many_workers_were_sampled',
    valueType: 'number',
    category: 'Key Metrics',
  },

  // --- Gender ---
  {
    key: 'number_of_male_workers',
    auditLabel: 'Male workers',
    dataPointId: 'number_of_male_workers',
    valueType: 'number',
    category: 'Gender',
  },
  {
    key: 'number_of_female_workers',
    auditLabel: 'Female workers',
    dataPointId: 'number_of_female_workers',
    valueType: 'number',
    category: 'Gender',
  },
  {
    key: 'number_of_diverse_workers',
    auditLabel: 'Non-binary workers',
    dataPointId: 'number_of_diverse_workers',
    valueType: 'number',
    category: 'Gender',
  },

  // --- Permanent Workers ---
  {
    key: 'number_of_male_permanent_workers',
    auditLabel: 'Permanent workers - Male',
    dataPointId: 'number_of_male_permanent_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_female_permanent_workers',
    auditLabel: 'Permanent workers - Female',
    dataPointId: 'number_of_female_permanent_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_diverse_permanent_workers',
    auditLabel: 'Permanent workers - Non-binary',
    dataPointId: 'number_of_diverse_permanent_workers',
    valueType: 'number',
    category: 'Employment type',
  },

  // --- Temporary Workers ---
  {
    key: 'number_of_male_temporary_workers',
    auditLabel: 'Temporary workers - Male',
    dataPointId: 'number_of_male_temporary_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_female_temporary_workers',
    auditLabel: 'Temporary workers - Female',
    dataPointId: 'number_of_female_temporary_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_diverse_temporary_workers',
    auditLabel: 'Temporary workers - Non-binary',
    dataPointId: 'number_of_diverse_temporary_workers',
    valueType: 'number',
    category: 'Employment type',
  },

  // --- Seasonal Workers ---
  {
    key: 'number_of_male_seasonal_workers',
    auditLabel: 'Seasonal workers - Male',
    dataPointId: 'number_of_male_seasonal_workers',
    valueType: 'number',
    category: 'Seasonal',
  },
  {
    key: 'number_of_female_seasonal_workers',
    auditLabel: 'Seasonal workers - Female',
    dataPointId: 'number_of_female_seasonal_workers',
    valueType: 'number',
    category: 'Seasonal',
  },
  {
    key: 'number_of_diverse_seasonal_workers',
    auditLabel: 'Seasonal workers - Non-binary',
    dataPointId: 'number_of_diverse_seasonal_workers',
    valueType: 'number',
    category: 'Seasonal',
  },

  // --- Management ---
  {
    key: 'num_of_male_managers',
    auditLabel: 'Management - Male',
    dataPointId: 'num_of_male_managers',
    valueType: 'number',
    category: 'Management',
  },
  {
    key: 'num_of_female_managers',
    auditLabel: 'Management - Female',
    dataPointId: 'num_of_female_managers',
    valueType: 'number',
    category: 'Management',
  },
  {
    key: 'num_of_diverse_managers',
    auditLabel: 'Management - Non-binary',
    dataPointId: 'num_of_diverse_managers',
    valueType: 'number',
    category: 'Management',
  },

  // --- Apprentices ---
  {
    key: 'number_of_male_trainees_apprentices_intern_workers',
    auditLabel: 'Apprentices - Male',
    dataPointId: 'number_of_male_trainees_apprentices_intern_workers',
    valueType: 'number',
    category: 'Apprentices',
  },
  {
    key: 'number_of_female_trainees_apprentices_intern_workers',
    auditLabel: 'Apprentices - Female',
    dataPointId: 'number_of_female_trainees_apprentices_intern_workers',
    valueType: 'number',
    category: 'Apprentices',
  },
  {
    key: 'number_of_diverse_trainees_apprentices_intern_workers',
    auditLabel: 'Apprentices - Non-binary',
    dataPointId: 'number_of_diverse_trainees_apprentices_intern_workers',
    valueType: 'number',
    category: 'Apprentices',
  },

  // --- Workers on Probation ---
  {
    key: 'number_of_male_workers_under_probation',
    auditLabel: 'Workers on probation - Male',
    dataPointId: 'number_of_male_workers_under_probation',
    valueType: 'number',
    category: 'Probation',
  },
  {
    key: 'number_of_female_workers_under_probation',
    auditLabel: 'Workers on probation - Female',
    dataPointId: 'number_of_female_workers_under_probation',
    valueType: 'number',
    category: 'Probation',
  },
  {
    key: 'number_of_diverse_workers_under_probation',
    auditLabel: 'Workers on probation - Non-binary',
    dataPointId: 'number_of_diverse_workers_under_probation',
    valueType: 'number',
    category: 'Probation',
  },

  // --- Night Shift ---
  {
    key: 'number_of_male_night_shift_workers',
    auditLabel: 'Workers with night shift - Male',
    dataPointId: 'number_of_male_night_shift_workers',
    valueType: 'number',
    category: 'Night shift',
  },
  {
    key: 'number_of_female_night_shift_workers',
    auditLabel: 'Workers with night shift - Female',
    dataPointId: 'number_of_female_night_shift_workers',
    valueType: 'number',
    category: 'Night shift',
  },
  {
    key: 'number_of_diverse_night_shift_workers',
    auditLabel: 'Workers with night shift - Non-binary',
    dataPointId: 'number_of_diverse_night_shift_workers',
    valueType: 'number',
    category: 'Night shift',
  },

  // --- Workers with Disabilities ---
  {
    key: 'number_of_male_workers_with_disability',
    auditLabel: 'Workers with disabilities - Male',
    dataPointId: 'number_of_male_workers_with_disability',
    valueType: 'number',
    category: 'Disabilities',
  },
  {
    key: 'number_of_female_workers_with_disability',
    auditLabel: 'Workers with disabilities - Female',
    dataPointId: 'number_of_female_workers_with_disability',
    valueType: 'number',
    category: 'Disabilities',
  },
  {
    key: 'number_of_diverse_workers_with_disability',
    auditLabel: 'Workers with disabilities - Non-binary',
    dataPointId: 'number_of_diverse_workers_with_disability',
    valueType: 'number',
    category: 'Disabilities',
  },

  // --- Domestic Migrant Workers ---
  {
    key: 'number_of_male_domestic_migrant_workers',
    auditLabel: 'Domestic migrant workers - Male',
    dataPointId: 'number_of_male_domestic_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_female_domestic_migrant_workers',
    auditLabel: 'Domestic migrant workers - Female',
    dataPointId: 'number_of_female_domestic_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_diverse_domestic_migrant_workers',
    auditLabel: 'Domestic migrant workers - Non-binary',
    dataPointId: 'number_of_diverse_domestic_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },

  // --- Foreign Migrant Workers ---
  {
    key: 'number_of_male_foreign_migrant_workers',
    auditLabel: 'Foreign migrant workers - Male',
    dataPointId: 'number_of_male_foreign_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_female_foreign_migrant_workers',
    auditLabel: 'Foreign migrant workers - Female',
    dataPointId: 'number_of_female_foreign_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_diverse_foreign_migrant_workers',
    auditLabel: 'Foreign migrant workers - Non-binary',
    dataPointId: 'number_of_diverse_foreign_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },

  // --- Hired Directly ---
  {
    key: 'number_of_male_workers_hired_directly',
    auditLabel: 'Workers hired directly - Male',
    dataPointId: 'number_of_male_workers_hired_directly',
    valueType: 'number',
    category: 'Hiring type',
  },
  {
    key: 'number_of_female_workers_hired_directly',
    auditLabel: 'Workers hired directly - Female',
    dataPointId: 'number_of_female_workers_hired_directly',
    valueType: 'number',
    category: 'Hiring type',
  },
  {
    key: 'number_of_diverse_workers_hired_directly',
    auditLabel: 'Workers hired directly - Non-binary',
    dataPointId: 'number_of_diverse_workers_hired_directly',
    valueType: 'number',
    category: 'Hiring type',
  },

  // --- Hired Indirectly ---
  {
    key: 'number_of_male_workers_hired_indirectly',
    auditLabel: 'Workers hired indirectly - Male',
    dataPointId: 'number_of_male_workers_hired_indirectly',
    valueType: 'number',
    category: 'Hiring type',
  },
  {
    key: 'number_of_female_workers_hired_indirectly',
    auditLabel: 'Workers hired indirectly - Female',
    dataPointId: 'number_of_female_workers_hired_indirectly',
    valueType: 'number',
    category: 'Hiring type',
  },
  {
    key: 'number_of_diverse_workers_hired_indirectly',
    auditLabel: 'Workers hired indirectly - Non-binary',
    dataPointId: 'number_of_diverse_workers_hired_indirectly',
    valueType: 'number',
    category: 'Hiring type',
  },

  // --- Unionised Workers ---
  {
    key: 'number_of_male_unionised_workers',
    auditLabel: 'Unionised workers - Male',
    dataPointId: 'number_of_male_unionised_workers',
    valueType: 'number',
    category: 'Union membership',
  },
  {
    key: 'number_of_female_unionised_workers',
    auditLabel: 'Unionised workers - Female',
    dataPointId: 'number_of_female_unionised_workers',
    valueType: 'number',
    category: 'Union membership',
  },
  {
    key: 'number_of_diverse_unionised_workers',
    auditLabel: 'Unionised workers - Non-binary',
    dataPointId: 'number_of_diverse_unionised_workers',
    valueType: 'number',
    category: 'Union membership',
  },

  // --- Workers under CBA ---
  {
    key: 'number_of_male_workers_under_cba',
    auditLabel: 'Workers under CBA - Male',
    dataPointId: 'number_of_male_workers_under_cba',
    valueType: 'number',
    category: 'CBA coverage',
  },
  {
    key: 'number_of_female_workers_under_cba',
    auditLabel: 'Workers under CBA - Female',
    dataPointId: 'number_of_female_workers_under_cba',
    valueType: 'number',
    category: 'CBA coverage',
  },
  {
    key: 'number_of_diverse_workers_under_cba',
    auditLabel: 'Workers under CBA - Non-binary',
    dataPointId: 'number_of_diverse_workers_under_cba',
    valueType: 'number',
    category: 'CBA coverage',
  },

  // --- Pregnancy and Parental Leave ---
  {
    key: 'number_of_pregnant_or_breastfeeding_workers',
    auditLabel: 'Pregnant workers',
    dataPointId: 'number_of_pregnant_or_breastfeeding_workers',
    valueType: 'number',
    category: 'Pregnancy and parental leave',
  },
  {
    key: 'number_of_male_workers_on_parental_leave',
    auditLabel: 'Workers on parental leave - Male',
    dataPointId: 'number_of_male_workers_on_parental_leave',
    valueType: 'number',
    category: 'Pregnancy and parental leave',
  },
  {
    key: 'number_of_female_workers_on_parental_leave',
    auditLabel: 'Workers on parental leave - Female',
    dataPointId: 'number_of_female_workers_on_parental_leave',
    valueType: 'number',
    category: 'Pregnancy and parental leave',
  },
  {
    key: 'number_of_diverse_workers_on_parental_leave',
    auditLabel: 'Workers on parental leave - Non-binary',
    dataPointId: 'number_of_diverse_workers_on_parental_leave',
    valueType: 'number',
    category: 'Pregnancy and parental leave',
  },

  // --- Wages ---
  {
    key: 'minimum_wage_agreed_on_cba_local_currency',
    auditLabel: 'Minimum wage agreed on CBA in local currency',
    dataPointId: 'minimum_wage_agreed_on_cba_local_currency',
    valueType: 'currency_with_year',
    category: 'Wages',
  },

  // --- Sample ---
  {
    key: 'payroll_male_workers_sampled',
    auditLabel: 'Sample - Male',
    dataPointId: 'payroll_male_workers_sampled',
    valueType: 'number',
    category: 'Sample',
  },
  {
    key: 'payroll_female_workers_sampled',
    auditLabel: 'Sample - Female',
    dataPointId: 'payroll_female_workers_sampled',
    valueType: 'number',
    category: 'Sample',
  },
  {
    key: 'payroll_diverse_workers_sampled',
    auditLabel: 'Sample - Non-binary',
    dataPointId: 'payroll_diverse_workers_sampled',
    valueType: 'number',
    category: 'Sample',
  },
];

/**
 * Maps SEDEX/SMETA audit prompt response fields to standardized datapoint IDs
 * from @retracedgmbh/data-points-lib.
 *
 * All SEDEX datapoints are numeric worker counts (valueType: 'number').
 */
export const SEDEX_DATAPOINT_MAP: readonly BsciDatapointMapping[] = [
  // --- Workers ---
  {
    key: 'number_of_workers',
    auditLabel: 'Total workers',
    dataPointId: 'number_of_workers',
    valueType: 'number',
    category: 'Workers',
  },
  {
    key: 'number_of_male_workers',
    auditLabel: 'Male workers',
    dataPointId: 'number_of_male_workers',
    valueType: 'number',
    category: 'Workers',
  },
  {
    key: 'number_of_female_workers',
    auditLabel: 'Female workers',
    dataPointId: 'number_of_female_workers',
    valueType: 'number',
    category: 'Workers',
  },

  // --- Employment type ---
  {
    key: 'number_of_permanent_workers',
    auditLabel: 'Permanent workers',
    dataPointId: 'number_of_permanent_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_male_permanent_workers',
    auditLabel: 'Permanent workers - Male',
    dataPointId: 'number_of_male_permanent_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_female_permanent_workers',
    auditLabel: 'Permanent workers - Female',
    dataPointId: 'number_of_female_permanent_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_temporary_workers',
    auditLabel: 'Temporary workers',
    dataPointId: 'number_of_temporary_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_male_temporary_workers',
    auditLabel: 'Temporary workers - Male',
    dataPointId: 'number_of_male_temporary_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_female_temporary_workers',
    auditLabel: 'Temporary workers - Female',
    dataPointId: 'number_of_female_temporary_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_contract_workers',
    auditLabel: 'Agency / contract workers',
    dataPointId: 'number_of_contract_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_male_contract_workers',
    auditLabel: 'Agency / contract workers - Male',
    dataPointId: 'number_of_male_contract_workers',
    valueType: 'number',
    category: 'Employment type',
  },
  {
    key: 'number_of_female_contract_workers',
    auditLabel: 'Agency / contract workers - Female',
    dataPointId: 'number_of_female_contract_workers',
    valueType: 'number',
    category: 'Employment type',
  },

  // --- Migrant workers ---
  {
    key: 'number_of_foreign_migrant_workers',
    auditLabel: 'Foreign migrant workers',
    dataPointId: 'number_of_foreign_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_male_foreign_migrant_workers',
    auditLabel: 'Foreign migrant workers - Male',
    dataPointId: 'number_of_male_foreign_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_female_foreign_migrant_workers',
    auditLabel: 'Foreign migrant workers - Female',
    dataPointId: 'number_of_female_foreign_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_domestic_migrant_workers',
    auditLabel: 'Domestic migrant workers',
    dataPointId: 'number_of_domestic_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_male_domestic_migrant_workers',
    auditLabel: 'Domestic migrant workers - Male',
    dataPointId: 'number_of_male_domestic_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },
  {
    key: 'number_of_female_domestic_migrant_workers',
    auditLabel: 'Domestic migrant workers - Female',
    dataPointId: 'number_of_female_domestic_migrant_workers',
    valueType: 'number',
    category: 'Migrant workers',
  },

  // --- Remote / home workers ---
  {
    key: 'number_of_male_workers_working_remotely_or_bring_work_home',
    auditLabel: 'Home workers - Male',
    dataPointId: 'number_of_male_workers_working_remotely_or_bring_work_home',
    valueType: 'number',
    category: 'Remote workers',
  },
  {
    key: 'number_of_female_workers_working_remotely_or_bring_work_home',
    auditLabel: 'Home workers - Female',
    dataPointId: 'number_of_female_workers_working_remotely_or_bring_work_home',
    valueType: 'number',
    category: 'Remote workers',
  },

  // --- Interviews ---
  {
    key: 'total_worker_interviews',
    auditLabel: 'Total worker interviews',
    dataPointId: 'total_worker_interviews',
    valueType: 'number',
    category: 'Interviews',
  },
];

/**
 * How an audit was created (origin). Orthogonal to `external_source` (which
 * integration) and to digitization (a USER audit can be digitized and stays
 * USER_GENERATED). `USER_GENERATED` is the column default for manual creates.
 */
export const AUDIT_GENERATED_BY = {
  USER_GENERATED: 'USER_GENERATED',
  AI_GENERATED: 'AI_GENERATED',
  API: 'API',
} as const;
export type AuditGeneratedBy =
  (typeof AUDIT_GENERATED_BY)[keyof typeof AUDIT_GENERATED_BY];

/**
 * Audit type IDs that are supported for the digitization process.
 */
export const DIGITIZATION_AUDIT_TYPE_IDS = [
  'BSCI',
  'SEDEX_SMETA',
  'GUB825FYUYF',
] as const;
export type DigitizationAuditTypeId =
  (typeof DIGITIZATION_AUDIT_TYPE_IDS)[number];

/**
 * Status values for a digitized audit record.
 */
export const DIGITIZATION_STATUS = {
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

/**
 * Abstract workflow step names per audit type.
 * These are stored in the `workflows` JSON column of DIGITIZED_AUDITS.
 * Concrete job names are resolved in the orchestration layer (backend).
 */
export const AUDIT_DIGITIZATION_WORKFLOW_STEPS: Record<
  DigitizationAuditTypeId,
  string[]
> = {
  BSCI: ['file_data_extraction', 'extract_section_data', 'extract_datapoints'],
  SEDEX_SMETA: [
    'file_data_extraction',
    'extract_section_data',
    'extract_datapoints',
  ],
  GUB825FYUYF: [
    'file_data_extraction',
    'extract_section_data',
    'extract_datapoints',
  ],
};
